<script setup>
import { ref, computed, onMounted } from 'vue';
import { router, usePage, Head } from '@inertiajs/vue3';
import { useVuelidate } from '@vuelidate/core';
import { required, email, helpers, maxLength } from '@vuelidate/validators';
import { THEMES } from '@/constants';
import Default from '@/Layouts/Default.vue';
import Modal from '@/Layouts/Modal.vue';
import Content from '@/Components/Content/index.vue';
import InputText from '@/Components/Input/Text.vue';
import InputTextArea from '@/Components/Input/TextArea.vue';
import InputRadio from '@/Components/Input/Radio.vue';
import InputCombobox from '@/Components/Input/Combobox.vue';
import Button from '@/Components/Button.vue';
import Icon from '@/Components/Icon/index.vue';
import ProviderSummary from '@/Components/Enquiry/ProviderSummary.vue'
import CourseSummary from '@/Components/Enquiry/CourseSummary.vue';
import ScholarshipSummary from '@/Components/Enquiry/ScholarshipSummary.vue';
import countryData from '@/Json/country-codes.json'
import AccordionGroup from '@/Components/Accordion/Group.vue';
import AccordionItem from '@/Components/Accordion/Item.vue';
import privacyContent from '@/Json/privacy.json';
import { previousRoute } from '@/composables/useNavigationTracker';

const props = defineProps({
  // eslint-disable-next-line vue/prop-name-casing
  app_form_id: {
    type: Number,
    required: true
  },
  enquiryType: {
    type: String,
    required: true,
  },
  course: {
    type: Object,
  },
  scholarship: {
    type: Object,
  },
  provider: {
    type: Object,
  },
});

const enquiryData = ref({
  app_form_id: props.app_form_id,
});

const page = usePage();

const context = computed(() => page.props?.context);
const providerName = context.value.name;
const theme = context.value.theme;

const isSchool = computed(() => {
  const organisation_type = props.provider?.organisation_type?.type || props.provider?.organisation_type;
  return typeof(organisation_type) === 'string' && organisation_type.includes('School')
});

const responseSuccess = computed(() => page.props.responseSuccess);
const responseError = computed(() => page.props.responseError);

const rules = computed(() => ({
  first_name: { required: helpers.withMessage('Tell us your first name', required), maxLength: maxLength(50) },
  last_name: { required: helpers.withMessage('Tell us your last name', required), maxLength: maxLength(50) },
  nationality: { required: helpers.withMessage('Where are you from?', required) },
  country: { required: helpers.withMessage('What country do you live in?', required) },
  city: { required: helpers.withMessage('What city do you live in?', required), maxLength: maxLength(50) },
  highest_study: isSchool.value ? {} : { required: helpers.withMessage('What is the highest level of study you have achieved?', required) },
  highest_qualification: isSchool.value ? {} : {  maxLength: maxLength(250) },
  highest_institution: isSchool.value ? {} : { maxLength: maxLength(250) },
  start_study: { required },
  course_grade: isSchool.value ? { required } : {},
  course_field: !isSchool.value && !props.course ? { required, maxLength: maxLength(250) } : {},
  course_level: !isSchool.value && !props.course ? { required } : {},
  funding_option: { required },
  country_code: { required: helpers.withMessage('Please enter the country code for your phone', required) },
  phone: { required: helpers.withMessage('Please enter a valid phone number', required), maxLength: maxLength(50) },
  email: { required: helpers.withMessage('Please enter a valid email', required), email, maxLength: maxLength(250) },
}))

const v$ = useVuelidate(rules, enquiryData);

const scrollHere = ref(null);

const cancelAction = () => {
  window.history.back();
};

const isoCodeMap = new Map(countryData.map((c) => [c.name, c.iso_code]));
const sendEnquiry = async () => {
  const ok = await v$.value.$validate();
  if (ok) {
    enquiryData.value.nationality_iso_code = isoCodeMap.get(enquiryData.value.nationality) || '';
    enquiryData.value.country_iso_code = isoCodeMap.get(enquiryData.value.country) || '';
    if (props.provider) {
      enquiryData.value.organisation_id = props.provider.organisation_id;
      enquiryData.value.organisation_name = props.provider.name;
      enquiryData.value.enquiry_type = 'provider';
    }
    if (props.course) {
      enquiryData.value.course_id = props.course.course_id;
      enquiryData.value.course_name = props.course.name;
      enquiryData.value.enquiry_type = 'course';
    }
    if (props.scholarship) {
      enquiryData.value.scholarship_id = props.scholarship.scholarship_id;
      enquiryData.value.scholarship_name = props.scholarship.name;
      enquiryData.value.enquiry_type = 'scholarship';
    }
    try {
      router.post('/enquiry/submit', enquiryData.value);
    } catch (error) {
      const { code, message, response: { data: { exception, message: responseMessage } = {} } = {} } = error;
      console.log('exception', { code, message, exception,  responseMessage});
    }
  }
};

const gradeOptions = [
  "Prep",
  "Kindergarden",
  "Grade 1",
  "Grade 2",
  "Grade 3",
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Year 7",
  "Year 8",
  "Year 9",
  "Year 10",
  "Year 11",
  "Year 12",
].map((c) => ({ value: c, label: c }))

const fieldOfStudyOptions = [
  { value: 'Accounting', label: 'Accounting' },
  { value: 'Architecture', label: 'Architecture' },
  { value: 'Built environment', label: 'Built environment' },
  { value: 'Business and management', label: 'Business and management' },
  { value: 'Communications', label: 'Communications' },
  { value: 'Computing and information technology', label: 'Computing and information technology' },
  { value: 'Creative arts', label: 'Creative arts' },
  { value: 'Environmental studies', label: 'Environmental studies' },
  { value: 'Economics', label: 'Economics' },
  { value: 'Education and training', label: 'Education and training' },
  { value: 'Engineering and technology', label: 'Engineering and technology' },
  { value: 'Rehabilitation', label: 'Rehabilitation' },
  { value: 'Health services and support', label: 'Health services and support' },
  { value: 'Tourism and hospitality', label: 'Tourism and hospitality' },
  { value: 'Humanities and social sciences', label: 'Humanities and social sciences' },
  { value: 'Languages', label: 'Languages' },
  { value: 'Law', label: 'Law' },
  { value: 'Para-legal studies', label: 'Para-legal studies' },
  { value: 'Pharmacy', label: 'Pharmacy' },
  { value: 'Sport and leisure', label: 'Sport and leisure' },
  { value: 'Mathematics', label: 'Mathematics' },
  { value: 'Nursing', label: 'Nursing' },
  { value: 'Psychology', label: 'Psychology' },
  { value: 'Sciences', label: 'Sciences' }
];

const startOptions = [
  { value: 'Within next 6 month', label: 'Within next 6 months' },
  { value: '6 - 12 months', label: '6 - 12 months' },
  { value: '12 months +', label: '12 months +' },
];

const fundingOptions = [
  { value: 'Self-funded', label: 'Self-funded' },
  { value: 'Scholarship', label: 'Scholarship' },
  { value: 'Grant or loan', label: 'Grant or loan' },
];

const levelOfStudyOptions = [
  { value: 'Diploma', label: 'Diploma' },
  { value: 'Bachelors', label: 'Bachelors' },
  { value: 'Masters', label: 'Masters' },
  { value: 'Research', label: 'Research' },
  { value: 'Other', label: 'Other' },
];

const countryOptions = countryData.map((c) => ({ value: c.name, label: c.name }));
const countryCodeOptions = countryData.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }));

// Could use `Back to ${page.props.history.previousRouteName}`
const goBackLabel = ref('Back');

const goBack = () => {
  if (previousRoute.value) {
    history.back();
  } else {
    window.location.href = context.value.home_url;
  }
}

const hrClasses = computed(() => [
  'w-full my-6',
  theme == THEMES.STUDY_MELB && 'invisible',
]);
const getElementById = (id) => document.getElementById(id);
</script>

<template>
  <Head>
    <title>Enquire - {{ providerName }}</title>
    <meta name="description" :content="`Enquire - ${providerName}`">
  </Head>
  <Default>
    <Content>
      <template #header>
        <Button variant="link" class="inline-flex items-center my-3" @click="goBack">
          <Icon path="ChevronLeft" :height="20" :width="20" class="inline-block"/>{{ goBackLabel }}</Button>
        <h1 class="font-au-sans-bold font-bold text-2xl">
          Enquiry information
        </h1>
        <p class=" text-sm"><em>All fields are required.</em></p>

        <hr :class="hrClasses">
        <Modal v-if="responseError" id="enquiry-failed-modal" type="error" @cancel="page.props.responseError = null" @close="page.props.responseError = null">
          <h1 class="mt-4 ml-8 font-au-sans-bold font-bold text-2xl">Uh oh! Something went wrong.</h1>
          <hr class="w-full my-6">
          <div class="mx-8">
            <p class="mb-2">We could not connect to {{ provider.name }} at the moment.</p>
            <p class="mb-2">Try again later.</p>
              <div
                ref="scrollHere"
                class="flex pb-5 justify-end gap-4"
              >
                <Button
                  id="responseErrorCancel"
                  variant="outlined"
                  type="button"
                  class="w-full md:w-fit "
                  @click="page.props.responseError = null"
                  @keydown.tab.shift.prevent="getElementById('responseErrorTryAgain').focus()"
                >
                  Cancel
                </Button>
                <Button
                  id="responseErrorTryAgain"
                  variant="primary"
                  type="button"
                  class="w-full md:w-fit"
                  @click="sendEnquiry"
                  @keydown.tab.prevent="getElementById('responseErrorCancel').focus()"
                >
                  Try again
                </Button>
              </div>
          </div>
        </Modal>
        <Modal v-if="responseSuccess" id="enquiry-sent-modal" type="success" @close="router.get('/courses')">
          <h1 class="mt-4 ml-8 font-au-sans-bold font-bold text-2xl">Enquiry sent!</h1>
          <hr class="w-full my-6">
          <div class="mx-8">
            <p class="mb-2">Enquiry has been successfully sent to {{ provider.name }}.</p>
            <Button
              id="enquiry-sent-ok"
              :link-data="provider.name"
              class="my-4 float-end"
              variant="outlined"
              type="button"
              @click="router.get('/courses')"
              @keydown.tab.prevent=""
              @keydown.tab.shift.prevent=""
            >Ok</Button>
          </div>
        </Modal>
      </template>
      <template #content>
        <div class="enquiry-form" :aria-hidden="responseError || responseSuccess">
          <fieldset class="grid sm:grid-cols-2 gap-4 pb-5">
            <legend class="sm:col-span-2 mb-4 font-au-sans-bold font-bold text-xl/1.25">Your details</legend>

            <InputText v-model="enquiryData.first_name" name="first_name" placeholder="First Name" label="First name" :validation="v$.first_name" />
            <InputText v-model="enquiryData.last_name" name="last_name" placeholder="Last Name" label="Last name" :validation="v$.last_name" />
            <InputCombobox
              v-model="enquiryData.country_code"
              :searchable="true"
              class="md:w-full"
              display-value
              name="country_code"
              label="Country code"
              placeholder="Country code"
              :options="countryCodeOptions"
              :validation="v$.country_code"
            />
            <InputText
              v-model="enquiryData.phone"
              name="phone"
              placeholder="Phone number"
              label="Phone number"
              :validation="v$.phone"
            />
            <InputText
              v-model="enquiryData.email"
              name="email"
              placeholder="Email address"
              label="Email address"
              :validation="v$.email"
            />
            <InputCombobox
              v-model="enquiryData.nationality"
              :searchable="true"
              class="md:w-full"
              name="nationality"
              label="Nationality"
              :options="countryOptions"
              :validation="v$.nationality"
            />
            <InputCombobox
              v-model="enquiryData.country"
              :searchable="true"
              class="md:w-full"
              name="country"
              label="Country of residence"
              :options="countryOptions"
              :validation="v$.country"
            />
            <InputText v-model="enquiryData.city" name="city" placeholder="City of Residence" label="City of residence" :validation="v$.city" />

            <hr class="sm:col-span-2 w-full" />
          </fieldset>
          <fieldset v-if="!isSchool" class="grid sm:grid-cols-2 gap-4 pb-5">
            <legend class="sm:col-span-2 mb-4 font-au-sans-bold font-bold text-xl/1.25">Educational background</legend>

            <InputRadio
              v-model="enquiryData.highest_study"
              class="sm:col-span-2 md:w-full"
              name="highest_study"
              label="What is the highest level of study gained?  (Please select Other for School level enquiries)"
              placeholder="Select"
              :options="levelOfStudyOptions"
              :validation="v$.highest_study"
            />
            <InputText
              v-model="enquiryData.highest_qualification"
              name="highest_qualification"
              placeholder="Name of qualification"
              label="Name of qualification (Not applicable for school enquiries)"
              :validation="v$.highest_qualification"
            />
            <InputText
              v-model="enquiryData.highest_institution"
              name="highest_institution"
              placeholder="Name of provider"
              label="Name of provider (Not applicable for school enquiries)"
              :validation="v$.highest_institution"
            />

            <hr class="sm:col-span-2 w-full" />
          </fieldset>
          <fieldset class="grid sm:grid-cols-2 gap-4 pb-5">
            <legend class="sm:col-span-2 mb-4 font-au-sans-bold font-bold text-xl/1.25">
              More information
            </legend>

            <InputRadio
              v-model="enquiryData.start_study"
              class="sm:col-span-2 md:w-full"
              name="start_study"
              label="When are you looking to start studying?"
              :options="startOptions"
              :validation="v$.start_study"
            />
            <InputCombobox
              v-if="isSchool"
              v-model="enquiryData.course_grade"
              :searchable="false"
              class="md:w-full"
              name="course_grade"
              label="Applying to enter grade/year level"
              :options="gradeOptions"
              :validation="v$.course_grade"
            />
            <InputCombobox
              v-if="!isSchool && !course"
              v-model="enquiryData.course_field"
              :searchable="true"
              class="md:w-full"
              name="course_field"
              label="Field of study"
              :options="fieldOfStudyOptions"
              :validation="v$.course_field"
            />
            <InputCombobox
              v-if="!isSchool && !course"
              v-model="enquiryData.course_level"
              :searchable="false"
              class="md:w-full"
              name="course_level"
              label="Level of study"
              :options="levelOfStudyOptions"
              :validation="v$.course_level"
            />
            <InputRadio
              v-model="enquiryData.funding_option"
              class="sm:col-span-2 md:w-full"
              name="funding_option"
              label="How would you fund your studies?"
              :options="fundingOptions"
              :validation="v$.funding_option"
            />
            <InputTextArea
              v-model="enquiryData.comments"
              class="sm:col-span-2"
              name="comments"
              :placeholder="`Message / Question${ provider ? ' to ' + provider.name : '' }`"
              label="Comments"
              :validation="v$.comments"
            />

            <hr class="sm:col-span-2 w-full" />
          </fieldset>
          <div class="pb-5">
            <div
              ref="scrollHere"
              class="flex gap-4 text-primary-600"
            >
              <Button
                variant="link"
                type="button"
                class="w-full md:w-fit "
                @click="cancelAction"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="button"
                :disabled="responseSuccess"
                class="w-full md:w-fit"
                @click="sendEnquiry"
              >
                Send Email
              </Button>
            </div>
            <div
              v-if="v$.$error"
              class="text-red text-sm"
            >
              Please correct the above errors before submitting.
            </div>
          </div>
          <div class="text-primary">
            <div class="px-5">
              <p class="h6 font-bold mb-3">{{ privacyContent[theme]['intro'] }}</p>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <ul v-if="privacyContent[theme]['preamble']" class="list-disc ml-6" v-html="privacyContent[theme]['preamble']"></ul>
            </div>
            <AccordionGroup>
              <AccordionItem>
                <template #title>
                  <p class="h6 font-bold">How we will use your personal information:</p>
                </template>
                <template #content>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div v-html="privacyContent[theme]['policy']"></div>
                </template>
              </AccordionItem>
            </AccordionGroup>
          </div>
        </div>
      </template>
      <template #sidepanel>
        <ProviderSummary
          v-if="provider"
          :provider="provider"
        />
        <CourseSummary
          v-if="course"
          :course="course"
        />
        <ScholarshipSummary
          v-if="scholarship"
          :scholarship="scholarship"
        />
      </template>
    </Content>
  </Default>
</template>
